import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Module } from "@/constants";
import Title from "@/components/Title";
import { List } from "@/components/list/List";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import {
  laborCostsInStoresDepartmentBasedRead,
  laborCostsInStoresRoleBasedRead,
  ReadLaborCostsInStores,
} from "@/schemas/laborCostsInStores";
import { laborCostsInStoresDepartmentBased } from "@/services/laborCostsInStoresDepartmentBased";
import { laborCostsInStoresRoleBased } from "@/services/laborCostsInStoresRoleBased";
import { LaborCostsInStoresForm } from "@/modules/projects/components/invoiceSpecifications/section/LaborCostsInStoresForm";
import {
  InvoiceSpecificationStoresForm,
  invoiceSpecificationStoresFormSchema,
} from "@/schemas/invoiceSpecificationStoresForm";
import { useLaborCostsInStoresColumnOverrides } from "@/modules/projects/components/invoiceSpecifications/hooks/useLaborCostsInStoresColumnOverrides";
import { useLaborCostsInStoresColumnVisibility } from "@/modules/projects/components/invoiceSpecifications/hooks/useLaborCostsInStoresColumnVisibility";
import { LaborCostsEntityType } from "@/modules/projects/components/invoiceSpecifications/types/LaborCostsEntityType";
import { WriteInvoiceSpecificationStores } from "@/schemas/invoiceSpecificationForm";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ZodReadonly, ZodObject, ZodRawShape } from "zod";
import { toast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useQueryData from "@/hooks/useQueryData";
import { useLaborCostsInStoresFormDefaultValues } from "../hooks/useLaborCostsInStoresFormDefaultValues";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { DataTableQueryContext } from "@/components/data-table/DataTableQueryContext";

interface LaborCostsService {
  getList: (
    settlementId: string,
    fetchParams?: FetchListRequest<ReadLaborCostsInStores>,
  ) => Promise<SuccessFetchListResponse<ReadLaborCostsInStores>>;
  patch: (
    invoiceSpecificationId: string,
    values: WriteInvoiceSpecificationStores,
  ) => Promise<unknown>;
}

interface LaborCostsInStoresSectionComponentProps {
  invoiceSpecificationId: string;
  projectDependsOn: LaborCostsEntityType;
}

const LaborCostsInStoresSectionComponent = ({
  invoiceSpecificationId,
  projectDependsOn,
}: LaborCostsInStoresSectionComponentProps) => {
  const nameForTranslations = "laborCostsInStores";
  const { t } = useTranslation();

  const [watchedStores, setWatchedStores] = useState<
    InvoiceSpecificationStoresForm["stores"] | undefined
  >();

  const config: {
    name: string;
    schema: ZodReadonly<ZodObject<ZodRawShape>>;
    service: LaborCostsService;
    translationKey: string;
  } = useMemo(() => {
    if (projectDependsOn === "department") {
      return {
        name: "laborCostsInStoresDepartmentBased",
        schema: laborCostsInStoresDepartmentBasedRead,
        service: laborCostsInStoresDepartmentBased,
        translationKey: "departmentBased",
      };
    } else {
      return {
        name: "laborCostsInStoresRoleBased",
        schema: laborCostsInStoresRoleBasedRead,
        service: laborCostsInStoresRoleBased,
        translationKey: "roleBased",
      };
    }
  }, [projectDependsOn]);

  const options = useCreateDataTableOptions<ReadLaborCostsInStores>(
    config.name,
  );

  const queryKey = useDynamicQueryParams(config.name, options);
  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      config.service.getList(invoiceSpecificationId, {
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [invoiceSpecificationId, options.filters, options.sorting, config.service],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadLaborCostsInStores>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
  const queryValue = useMemo(() => ({ query }), [query]);
  const readLaborCostsInStoresData = useQueryData(queryValue.query) || [];
  const defaultValues = useLaborCostsInStoresFormDefaultValues(
    readLaborCostsInStoresData,
  );
  const invoiceSpecificationsStoreForm =
    useForm<InvoiceSpecificationStoresForm>({
      resolver: zodResolver(invoiceSpecificationStoresFormSchema),
      defaultValues,
    });

  const handleIncludeOnInvoiceChange = useCallback(
    (stores: InvoiceSpecificationStoresForm["stores"]) => {
      setWatchedStores(() => ({ ...stores }));
    },
    [],
  );

  const { baseline, updateBaseline } = useFormBaseline(
    invoiceSpecificationsStoreForm,
    defaultValues,
  );

  const getRowProps = useCallback(
    function <T>(row: { original: T }): Record<string, unknown> {
      const original = row.original as unknown as {
        id: string;
        include_on_invoice: boolean;
      };
      const storeId = original.id;
      const includeOnInvoice =
        watchedStores && storeId
          ? watchedStores[storeId]?.include_on_invoice ??
            original.include_on_invoice
          : original.include_on_invoice;

      return {
        className: includeOnInvoice
          ? "bg-green-50 hover:bg-green-100"
          : "bg-red-50 hover:bg-red-100",
      };
    },
    [watchedStores],
  );

  const columnOverrides = useLaborCostsInStoresColumnOverrides();
  const initialColumnVisibility = useLaborCostsInStoresColumnVisibility({
    config,
    projectDependsOn,
  });

  const tableFeatures = useMemo(
    () => ({
      editionHighlight: { baseline, recordKey: "stores" },
      exportSettings: { moduleName: Module.InvoiceSpecifications },
      initialColumnVisibility,
      getRowProps,
    }),
    [baseline, initialColumnVisibility, getRowProps],
  );

  const editLaborCostsInStores = async (
    values: WriteInvoiceSpecificationStores,
  ) => {
    await config.service.patch(invoiceSpecificationId, values);
  };

  const queryClient = useQueryClient();

  const editLaborCostsInStoresMutation = useMutationWithErrorHandling({
    mutationFn: editLaborCostsInStores,
    onSuccess: () => {
      updateBaseline();
      toast({ title: t("laborCostsInStores.success.update") });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(invoiceSpecificationId) ||
          query.queryKey.includes("invoiceSpecification"),
      });
    },
    errorTitle: t("laborCostsInStores.error.update"),
  });

  const handleEditLaborCostsInStores = useCallback(
    async (
      values: InvoiceSpecificationStoresForm["stores"],
      dirtyFields: Record<string, unknown>,
    ) => {
      const patchData: WriteInvoiceSpecificationStores = {
        stores: Object.entries(dirtyFields).reduce(
          (acc, [key, isDirty]) => {
            if (isDirty && values[key]) {
              const {
                base_amount,
                invoiced_amount,
                planned_hours,
                store_id,
                ...storeData
              } = values[key];
              acc.push(storeData);
            }
            return acc;
          },
          [] as NonNullable<WriteInvoiceSpecificationStores["stores"]>,
        ),
      };

      await editLaborCostsInStoresMutation.mutateAsync(patchData);
    },
    [editLaborCostsInStoresMutation],
  );

  return (
    <List
      schema={config.schema}
      name={nameForTranslations}
      query={query}
      options={options}
      tableFeatures={tableFeatures}
      columnOverrides={columnOverrides}
    >
      <div className="py-2">
        <Title>{t(`laborCostsInStores.${config.translationKey}.header`)}</Title>
      </div>
      <div>
        <List.Menubar className="mb-2 grow">
          <List.Menubar.Columns />
          <List.Menubar.Reset />
        </List.Menubar>
        <DataTableQueryContext.Provider value={queryValue}>
          <LaborCostsInStoresForm
            onIncludeOnInvoiceChange={handleIncludeOnInvoiceChange}
            onEditLaborCostsInStores={handleEditLaborCostsInStores}
            form={invoiceSpecificationsStoreForm}
            defaultValues={defaultValues}
          />
        </DataTableQueryContext.Provider>
      </div>
    </List>
  );
};

export const LaborCostsInStoresSection = memo(
  LaborCostsInStoresSectionComponent,
);
